// Shared, pure Strava contracts. No credentials or network access in this module.
export type RouteActivity = "run" | "ride" | "walk" | "ski" | "swim";
export type Coordinate = [number, number]; // latitude, longitude
export type RouteGeometry = {
  coordinates: Coordinate[];
  distance_m: number;
  name: string;
};
export type RouteSuggestion = RouteGeometry & {
  id: string;
  activity_id: RouteActivity;
  frequency: number;
  last_used_at: string;
};
export type DetectedRoute = Omit<RouteSuggestion, "id"> & {
  source_activity_id: string;
  member_ids: string[];
};
export type StravaActivity = {
  id: string;
  name: string;
  start_date: string;
  sport_type: string;
  summary_polyline: string;
  trainer: boolean;
  manual: boolean;
};
export type StravaLink = { activity_url: string; source: "auto" | "manual" };
export type ConnectionStatus = {
  connected: boolean;
  sync_state: "pending" | "syncing" | "ready" | "error" | "disconnected";
  imported_count: number;
  route_count: number;
  last_synced_at: string | null;
  error: string | null;
};
export const ROUTE_RULES = Object.freeze({
  version: 1,
  lengthDifference: 0.15,
  coverage: 0.9,
  toleranceMetres: 50,
  samples: 96,
});
const labels: Record<RouteActivity, string> = {
  run: "Løperute",
  ride: "Sykkelrute",
  walk: "Turrute",
  ski: "Skirute",
  swim: "Svømmerute",
};

export function activityCategory(sport: string): RouteActivity | null {
  if (["Run", "TrailRun"].includes(sport)) return "run";
  if (
    [
      "Ride",
      "MountainBikeRide",
      "GravelRide",
      "EBikeRide",
      "EMountainBikeRide",
    ].includes(sport)
  )
    return "ride";
  if (["Walk", "Hike"].includes(sport)) return "walk";
  if (["NordicSki", "BackcountrySki", "AlpineSki"].includes(sport))
    return "ski";
  return sport === "Swim" ? "swim" : null;
}

// Tokenize strings as well as numbers so digits inside names/escaped JSON strings
// are never rewritten. Unsafe JSON integers remain exact decimal strings.
export function parseStravaJson(text: string): unknown {
  return JSON.parse(
    text.replace(
      /"(?:\\.|[^"\\])*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g,
      (token) => {
        if (token[0] === '"' || !/^-?\d+$/.test(token)) return token;
        return Number.isSafeInteger(Number(token))
          ? token
          : JSON.stringify(token);
      },
    ),
  );
}
export function decimalId(value: unknown): string {
  if (typeof value === "number" && Number.isSafeInteger(value) && value > 0)
    return String(value);
  if (typeof value === "string" && /^[1-9]\d{0,24}$/.test(value)) return value;
  throw new Error("Invalid Strava identifier");
}
export function readActivity(value: unknown): StravaActivity {
  const a = value as Record<string, unknown>;
  const start = String(a.start_date ?? "");
  if (!Number.isFinite(Date.parse(start)))
    throw new Error("Invalid activity date");
  return {
    id: decimalId(a.id),
    name: String(a.name ?? "Aktivitet").slice(0, 200),
    start_date: start,
    sport_type: String(a.sport_type ?? a.type ?? ""),
    summary_polyline: String(
      (a.map as { summary_polyline?: string } | null)?.summary_polyline ?? "",
    ),
    trainer: a.trainer === true,
    manual: a.manual === true,
  };
}

export function decodePolyline(encoded: string): Coordinate[] {
  if (!encoded || encoded.length > 200_000) return [];
  const result: Coordinate[] = [];
  let cursor = 0,
    lat = 0,
    lng = 0;
  function read() {
    let value = 0,
      shift = 0,
      byte = 0;
    do {
      if (cursor >= encoded.length || shift > 30)
        throw new Error("Malformed polyline");
      byte = encoded.charCodeAt(cursor++) - 63;
      if (byte < 0 || byte > 63) throw new Error("Malformed polyline");
      value += (byte & 31) * 2 ** shift;
      shift += 5;
    } while (byte >= 32);
    return value % 2 ? -(Math.floor(value / 2) + 1) : value / 2;
  }
  try {
    while (cursor < encoded.length) {
      lat += read();
      lng += read();
      const p: Coordinate = [lat / 1e5, lng / 1e5];
      if (Math.abs(p[0]) > 90 || Math.abs(p[1]) > 180 || result.length > 20_000)
        return [];
      const last = result[result.length - 1];
      if (!last || last[0] !== p[0] || last[1] !== p[1]) result.push(p);
    }
  } catch {
    return [];
  }
  return result.length >= 2 ? result : [];
}
const rad = Math.PI / 180;
export function distance(a: Coordinate, b: Coordinate): number {
  const dlat = (b[0] - a[0]) * rad,
    dlng = (b[1] - a[1]) * rad;
  const h =
    Math.sin(dlat / 2) ** 2 +
    Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dlng / 2) ** 2;
  return 12_742_000 * Math.asin(Math.min(1, Math.sqrt(h)));
}
export function pathLength(points: Coordinate[]): number {
  return points.reduce(
    (sum, p, i) => sum + (i ? distance(points[i - 1], p) : 0),
    0,
  );
}
export function distanceToPath(p: Coordinate, path: Coordinate[]): number {
  let minimum = Infinity;
  const scale = Math.cos(p[0] * rad);
  const xy = (q: Coordinate) => [
    (((q[1] - p[1] + 540) % 360) - 180) * rad * 6_371_000 * scale,
    (q[0] - p[0]) * rad * 6_371_000,
  ];
  for (let i = 1; i < path.length; i++) {
    const a = xy(path[i - 1]),
      b = xy(path[i]);
    const x = b[0] - a[0],
      y = b[1] - a[1],
      length = x * x + y * y;
    const t = length
      ? Math.max(0, Math.min(1, -(a[0] * x + a[1] * y) / length))
      : 0;
    minimum = Math.min(minimum, Math.hypot(a[0] + t * x, a[1] + t * y));
  }
  return minimum;
}
function samples(path: Coordinate[], length: number): Coordinate[] {
  const out: Coordinate[] = [path[0]];
  let segment = 1,
    accumulated = 0;
  for (let i = 1; i < ROUTE_RULES.samples; i++) {
    const target = (length * i) / (ROUTE_RULES.samples - 1);
    while (
      segment < path.length - 1 &&
      accumulated + distance(path[segment - 1], path[segment]) < target
    ) {
      accumulated += distance(path[segment - 1], path[segment++]);
    }
    const a = path[segment - 1],
      b = path[segment];
    const t = Math.min(1, (target - accumulated) / (distance(a, b) || 1));
    out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
  }
  return out;
}
type Candidate = {
  activity: StravaActivity;
  category: RouteActivity;
  points: Coordinate[];
  sampled: Coordinate[];
  length: number;
  bounds: number[];
};
function candidate(a: StravaActivity): Candidate | null {
  const category = activityCategory(a.sport_type);
  if (!category || a.trainer || a.manual) return null;
  const points = decodePolyline(a.summary_polyline),
    length = pathLength(points);
  if (points.length < 2 || length < 50) return null;
  const bounds = [90, 180, -90, -180];
  for (const p of points) {
    bounds[0] = Math.min(bounds[0], p[0]);
    bounds[1] = Math.min(bounds[1], p[1]);
    bounds[2] = Math.max(bounds[2], p[0]);
    bounds[3] = Math.max(bounds[3], p[1]);
  }
  return {
    activity: a,
    category,
    points,
    length,
    bounds,
    sampled: samples(points, length),
  };
}
function difference(a: Candidate, b: Candidate): number {
  if (
    a.category !== b.category ||
    Math.abs(a.length - b.length) / Math.max(a.length, b.length) >
      ROUTE_RULES.lengthDifference
  )
    return Infinity;
  const latitudeMargin = ROUTE_RULES.toleranceMetres / 111_000;
  const longitudeMargin =
    latitudeMargin / Math.max(0.01, Math.cos(a.points[0][0] * rad));
  if (
    a.bounds[0] > b.bounds[2] + latitudeMargin ||
    b.bounds[0] > a.bounds[2] + latitudeMargin ||
    a.bounds[1] > b.bounds[3] + longitudeMargin ||
    b.bounds[1] > a.bounds[3] + longitudeMargin
  )
    return Infinity;
  let total = 0;
  for (const [from, to] of [
    [a, b],
    [b, a],
  ]) {
    let covered = 0;
    for (const p of from.sampled) {
      const d = distanceToPath(p, to.points);
      total += d;
      if (d <= ROUTE_RULES.toleranceMetres) covered++;
    }
    if (covered / from.sampled.length < ROUTE_RULES.coverage) return Infinity;
  }
  return total / (2 * ROUTE_RULES.samples);
}
export function buildRouteSuggestions(
  activities: StravaActivity[],
): DetectedRoute[] {
  const candidates = [...new Map(activities.map((a) => [a.id, a])).values()]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map(candidate)
    .filter((c): c is Candidate => c !== null);
  const groups: Candidate[][] = [],
    distances = new Map<string, number>();
  function compare(a: Candidate, b: Candidate) {
    const key = [a.activity.id, b.activity.id].sort().join(":");
    if (!distances.has(key)) distances.set(key, difference(a, b));
    return distances.get(key)!;
  }
  for (const c of candidates) {
    const group = groups.find((g) =>
      g.every((member) => Number.isFinite(compare(c, member))),
    );
    if (group) group.push(c);
    else groups.push([c]);
  }
  return groups
    .map((group) => {
      const scores = group.map((c) => ({
        c,
        score: group.reduce(
          (sum, other) => sum + (c === other ? 0 : compare(c, other)),
          0,
        ),
      }));
      scores.sort(
        (a, b) =>
          a.score - b.score ||
          b.c.activity.start_date.localeCompare(a.c.activity.start_date) ||
          a.c.activity.id.localeCompare(b.c.activity.id),
      );
      const rep = scores[0].c;
      return {
        activity_id: rep.category,
        source_activity_id: rep.activity.id,
        member_ids: group.map((c) => c.activity.id),
        name: `${labels[rep.category]} ${(rep.length / 1000).toFixed(1).replace(".", ",")} km`,
        coordinates: rep.points,
        distance_m: Math.round(rep.length),
        frequency: group.length,
        last_used_at: group
          .map((c) => c.activity.start_date)
          .sort()
          .reverse()[0],
      };
    })
    .sort(
      (a, b) =>
        b.frequency - a.frequency ||
        b.last_used_at.localeCompare(a.last_used_at) ||
        a.source_activity_id.localeCompare(b.source_activity_id),
    );
}
export function rankSuggestions(
  routes: RouteSuggestion[],
  meeting?: Coordinate | null,
): RouteSuggestion[] {
  return [...routes].sort(
    (a, b) =>
      (meeting
        ? Number(distanceToPath(meeting, b.coordinates) <= 5000) -
          Number(distanceToPath(meeting, a.coordinates) <= 5000)
        : 0) ||
      b.frequency - a.frequency ||
      b.last_used_at.localeCompare(a.last_used_at) ||
      a.id.localeCompare(b.id),
  );
}
export type MatchPlan = { id: string; starts_at: string; ends_at: string };
export function activityInWindow(
  plan: MatchPlan,
  activity: StravaActivity,
  hours: number,
): boolean {
  const start = Date.parse(activity.start_date);
  return (
    start >= Date.parse(plan.starts_at) - hours * 3_600_000 &&
    start <= Date.parse(plan.ends_at) + hours * 3_600_000
  );
}
export function matchActivities(
  plans: MatchPlan[],
  activities: StravaActivity[],
): { plan_id: string; activity_id: string }[] {
  const unique = [...new Map(activities.map((a) => [a.id, a])).values()];
  return plans.flatMap((plan) => {
    const matches = unique.filter((a) => activityInWindow(plan, a, 2));
    if (
      matches.length !== 1 ||
      plans.some(
        (other) =>
          other.id !== plan.id && activityInWindow(other, matches[0], 2),
      )
    )
      return [];
    return [{ plan_id: plan.id, activity_id: matches[0].id }];
  });
}
